import React, { useState, useEffect } from 'react';
import { Card, CardTitle, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const SYNC_DIR = process.env.REACT_APP_SYNC_DIR || 'sanoire-sync-new';

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => { const h = () => setMob(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return mob;
}

const SHEETS = [
  { label: 'Asia',        url: 'https://docs.google.com/spreadsheets/d/1uQgL_q_fPqCX6JKl1TuTVKDBHqVNwNxvnAt5JDjb2fk' },
  { label: 'Americas',    url: 'https://docs.google.com/spreadsheets/d/1kK94-IaJ_3QJKRa8BphoXuGOsKvynUNzRTkTRvNSWGU' },
  { label: 'Europe',      url: '' },
  { label: 'Africa',      url: '' },
  { label: 'Middle East', url: '' },
];

const SYNC_CMDS = [
  { label: 'Sync Cambodia (Asia sheet)',     cmd: 'node scripts/pipeline.js --country="Cambodia" --continuous' },
  { label: 'Sync full Asia sheet',           cmd: 'node scripts/pipeline.js --continuous' },
  { label: 'Verify only (no writes)',        cmd: 'node scripts/pipeline.js --verify-only --country="Cambodia" --continuous' },
  { label: 'Geocode Cambodia',               cmd: 'node scripts/geocode.js --country="Cambodia" --continuous' },
  { label: 'Geocode all (full run)',         cmd: 'node scripts/geocode.js --continuous' },
  { label: 'Enrich URLs (Haiku pass)',       cmd: 'node scripts/geocode.js --enrich-urls --country="Cambodia" --continuous' },
];

export default function SyncTrigger() {
  const isMobile = useIsMobile();
  const [freshStats, setFreshStats] = useState(null);
  const [copied,     setCopied]     = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.rpc('get_admin_stats');
        if (data) setFreshStats(data);
      } catch (_e) {}
    })();
  }, []);

  function copy(cmd, label) {
    const full = `cd ${SYNC_DIR}\n${cmd}`;
    navigator.clipboard.writeText(full);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  }

  return (
    <div style={{ fontFamily:F, padding: isMobile ? '16px 14px 80px' : '24px 28px' }}>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:'1.1rem', fontWeight:500, margin:0, color:'var(--color-text-primary)' }}>Sync & pipeline</h2>
        <p style={{ fontSize:'.78rem', color:'var(--color-text-secondary)', margin:'4px 0 0' }}>
          Trigger data syncs from Google Sheets, run geocoding, and manage the data pipeline.
        </p>
      </div>

      <Insight type="info" style={{marginBottom:16}}>
        Pipeline runs locally. Set the REACT_APP_SYNC_DIR environment variable to specify the sync directory. Copy any command below and paste into a terminal.
      </Insight>

      {/* Data freshness */}
      {freshStats && (
        <Card style={{ marginBottom:16 }}>
          <CardTitle>Data freshness</CardTitle>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10 }}>
            {[
              { label:'Total', val: (freshStats.total||0).toLocaleString(), color:'#314F5C' },
              { label:'Fresh (<90d)', val: (freshStats.fresh||0).toLocaleString(), color:'#28a745' },
              { label:'Stale (>90d)', val: (freshStats.stale||0).toLocaleString(), color:'#fd7e14' },
              { label:'Never verified', val: (freshStats.never||0).toLocaleString(), color:'#dc3545' },
            ].map(({label,val,color}) => (
              <div key={label} style={{ textAlign:'center', padding:'10px 8px', background:'var(--color-background-tertiary)', borderRadius:8 }}>
                <div style={{ fontSize:'.65rem', color:'var(--color-text-secondary)', marginBottom:4, textTransform:'uppercase', letterSpacing:'.04em' }}>{label}</div>
                <div style={{ fontSize:'1.2rem', fontWeight:500, color }}>{val}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Google Sheets sources */}
      <Card style={{ marginBottom:16 }}>
        <CardTitle>Source spreadsheets</CardTitle>
        <p style={{ fontSize:'.72rem', color:'var(--color-text-secondary)', margin:'0 0 12px' }}>Google Sheets are the source of truth. Each continent is a separate file with country tabs.</p>
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {SHEETS.map(({label,url}) => (
            <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'.5px solid var(--color-border-tertiary)' }}>
              <span style={{ fontSize:'.78rem', fontWeight:500, color:'var(--color-text-primary)' }}>{label}</span>
              {url ? <a href={url} target="_blank" rel="noreferrer" style={{ fontSize:'.72rem', color:'#6F8FA7' }}>Open sheet →</a>
                   : <span style={{ fontSize:'.72rem', color:'var(--color-text-secondary)' }}>URL not configured</span>}
            </div>
          ))}
        </div>
      </Card>

      {/* Terminal commands */}
      <Card>
        <CardTitle>Pipeline commands</CardTitle>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {SYNC_CMDS.map(({label,cmd}) => (
            <div key={label} style={{ background:'var(--color-background-tertiary)', borderRadius:8, padding:'10px 14px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
                <span style={{ fontSize:'.75rem', fontWeight:500, color:'var(--color-text-primary)' }}>{label}</span>
                <button onClick={() => copy(cmd, label)}
                  style={{ fontSize:'.65rem', color: copied===label?'#28a745':'#6F8FA7', background:'none', border:'1px solid var(--color-border-secondary)', borderRadius:4, padding:'2px 8px', cursor:'pointer', fontFamily:F }}>
                  {copied===label ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <code style={{ fontSize:'.7rem', color:'#AFE1F6', background:'#0d1d2b', borderRadius:6, padding:'6px 10px', display:'block', overflowX:'auto', whiteSpace:'nowrap' }}>
                {cmd}
              </code>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
